// DT1 dev order — the Alt/Cmd+K dev-tools dialog, NON-PRODUCTION ONLY: the v3
// FeaturesModal panel comes back (reverses RB1's kill). One hotkey, one
// dialog, mounted once at the App root (v3 mounted per-shell: sider +
// ExperienceLayout).
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DevToolsFeaturesPanel } from '@/components/ap/devtools/DevToolsFeaturesPanel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCurrentUser } from '@/hooks/dataHelpers';
import { isProduction } from '@/lib/appConstants';

export const DevToolsDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Fixed height — the features list is long enough to need it.
        className="flex h-[85vh] flex-col sm:max-w-3xl"
        data-testid="devtools-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('DevToolsDialog.dev_tools')}</DialogTitle>
          <DialogDescription>{t('DevToolsDialog.non_production_tooling')}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <DevToolsFeaturesPanel />
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Global Alt/Cmd+K host — v3 sider combo (k or K). Production mounts
 *  nothing. */
export const DevToolsHotkey = () => {
  const currentUser = useCurrentUser();
  const [open, setOpen] = useState(false);

  const available = currentUser?.role === 'admin' && !isProduction;

  const handleKeyboardShortcuts = useCallback((e: KeyboardEvent) => {
    if ((e.altKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (available) {
      document.addEventListener('keydown', handleKeyboardShortcuts);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcuts);
    };
  }, [available, handleKeyboardShortcuts]);

  if (!available || !open) return null;
  return <DevToolsDialog open={open} onOpenChange={setOpen} />;
};
